import { motion } from "framer-motion";
import { Microscope, ScissorsLineDashed, Sprout, Droplets, HeartPulse } from "lucide-react";
import { SectionHead } from "./SectionHead";
import { Reveal } from "./Reveal";
import { Wavelengths } from "./Wavelengths";

const Card = ({ className = "", children, delay = 0, testid }) => (
  <Reveal delay={delay} className={className}>
    <motion.div
      whileHover={{ y: -6 }}
      transition={{ type: "spring", stiffness: 200, damping: 20 }}
      className="h-full rounded-[1.75rem] border border-[#1B3B28]/10 bg-[#FCFBF7] p-8 flex flex-col"
      data-testid={testid}
    >
      {children}
    </motion.div>
  </Reveal>
);

const Icon = ({ I }) => (
  <span className="grid place-items-center w-12 h-12 rounded-full bg-[#E6ECE4] text-[#1B3B28]"><I size={20} strokeWidth={1.6} /></span>
);

const LaserHero = () => (
  <Reveal className="md:col-span-7 md:row-span-2">
    <div className="relative h-full min-h-[440px] rounded-[1.75rem] bg-[#0F2317] p-8 sm:p-10 overflow-hidden flex flex-col justify-end" data-testid="bento-laser-card">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_75%_25%,rgba(134,200,155,0.28),transparent_55%)]" />
      <motion.div
        className="absolute left-0 right-0 top-[32%] h-px bg-gradient-to-r from-transparent via-[#86C89B] to-transparent shadow-[0_0_30px_4px_rgba(134,200,155,0.6)]"
        animate={{ scaleX: [0.2, 1, 0.2], opacity: [0.3, 1, 0.3] }}
        transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
      />
      <span className="absolute top-8 right-8 f-mono text-xs text-[#86C89B]/80 tracking-[0.2em]">450 · 635 · 808 nm</span>
      <p className="relative f-serif text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-[-0.01em] text-outline select-none max-w-xl" style={{ WebkitTextStroke: "1px rgba(134,200,155,0.6)" }} data-testid="bento-laser-tagline">
        La terza generazione <em>del tuo sorriso</em>
      </p>
      <div className="relative mt-4">
        <p className="eyebrow text-[#86C89B]">Laser a tre lunghezze d'onda</p>
        <h3 className="f-serif text-3xl sm:text-4xl text-[#FCFBF7] mt-3 max-w-md">Decontamina le tasche parodontali in profondità.</h3>
        <p className="text-[#FCFBF7]/65 mt-4 max-w-lg leading-relaxed">
          Tre luci diverse, ognuna con un compito preciso: agiscono contro i batteri patogeni, riducono l'infiammazione e
          creano le condizioni ideali perché i tessuti guariscano.
        </p>
      </div>
    </div>
  </Reveal>
);

export const LaserBento = () => (
  <section id="laser" className="relative py-28 sm:py-40 bg-[#E6ECE4]" data-testid="laser-section">
    <div className="mx-auto max-w-[1400px] px-6 sm:px-10">
      <div className="grid lg:grid-cols-12 gap-10 items-end">
        <div className="lg:col-span-7">
          <SectionHead eyebrow="La terapia laser-assistita" lines={["Tecnologia che", "rispetta i tessuti,", "e il tuo tempo."]} />
        </div>
        <Reveal className="lg:col-span-4 lg:col-start-9" delay={0.2}>
          <p className="text-[#526356] leading-relaxed">
            Nella grande maggioranza dei casi la parodontite si cura senza chirurgia. Il segreto è unire la precisione del microscopio
            operatorio alla potenza del laser, trattando la bocca come un unico ambiente.
          </p>
        </Reveal>
      </div>
      <div className="mt-16 grid gap-5 md:grid-cols-12 md:auto-rows-[minmax(210px,auto)]">
        <LaserHero />
        <Card className="md:col-span-5" delay={0.1} testid="bento-microscope-card">
          <Icon I={Microscope} />
          <h3 className="f-serif text-3xl text-[#1B3B28] mt-auto pt-8">Aiuta nella rimozione del tartaro</h3>
          <p className="text-sm text-[#526356] mt-2 leading-relaxed">Il microscopio operatorio permette di rimuovere il tartaro sotto gengiva con sicurezza e precisione millimetrica, evitando il bisturi.</p>
        </Card>
        <Card className="md:col-span-5" delay={0.2} testid="bento-nosurgery-card">
          <Icon I={ScissorsLineDashed} />
          <h3 className="f-serif text-3xl text-[#1B3B28] mt-auto pt-8">Niente bisturi, niente punti</h3>
          <p className="text-sm text-[#526356] mt-2 leading-relaxed">Terapia non chirurgica e minimamente invasiva: pochi fastidi dopo la seduta, facilmente gestibili.</p>
        </Card>
        <Card className="md:col-span-4" delay={0.1} testid="bento-regeneration-card">
          <Icon I={Sprout} />
          <h3 className="f-serif text-2xl text-[#1B3B28] mt-auto pt-8">Possibile rigenerazione ossea</h3>
          <p className="text-sm text-[#526356] mt-2 leading-relaxed">In un ambiente privo di batteri l'osso può guarire, anche nei difetti verticali.</p>
        </Card>
        <Card className="md:col-span-4" delay={0.2} testid="bento-bleeding-card">
          <Icon I={Droplets} />
          <h3 className="f-serif text-2xl text-[#1B3B28] mt-auto pt-8">Sintomi che si attenuano presto</h3>
          <p className="text-sm text-[#526356] mt-2 leading-relaxed">Sanguinamento, pus, gonfiore e alitosi migliorano già dalle prime fasi.</p>
        </Card>
        <Card className="md:col-span-4" delay={0.3} testid="bento-systemic-card">
          <Icon I={HeartPulse} />
          <h3 className="f-serif text-2xl text-[#1B3B28] mt-auto pt-8">Adatta a chi ha altre patologie</h3>
          <p className="text-sm text-[#526356] mt-2 leading-relaxed">Mini-invasiva, è indicata anche per molti pazienti con patologie sistemiche o in terapia farmacologica.</p>
        </Card>
      </div>
      <Wavelengths />
    </div>
  </section>
);
